
import { defineComponent } from "vue";
import {
  IBienBanResponseBase,
  IVatTuBienBanKiemNghiemResponseBase,
} from "@/core/interfaces/ApiResponses";
import { BienBanKiemNghiemService } from "@/core/services/BienBanKiemNghiem.service";
import {
  formatQuantity,
  formatUnitPrice,
} from "@/core/helpers/number-format";
import { swalErrNotification } from "@/core/helpers/utils";
import { useDateTime } from "@/core/hooks/use-date-time-hook";
import PdfViewerPage from "@/components/shared/pdf-viewer/PdfViewerPage.vue";
import PdfDocumentHeader from "@/components/shared/pdf-viewer/PdfDocumentHeader.vue";
import { pickDonViPdfExtra } from "@/components/shared/pdf-viewer/pdf-document-header";
import {
  PDF_A4,
  PDF_CONTENT_MARGINS,
  getPdfContentWidthPx,
  getPdfPrintContentStyle,
} from "@/components/shared/pdf-viewer/pdf-content.config";
import html2canvas from "html2canvas";
// jsPDF is loaded as a global script via public/index.html to avoid webpack 4 binary parsing issues.
// The UMD bundle assigns to window.jspdf (lowercase) and the constructor lives at window.jspdf.jsPDF.
declare const window: Window & {
  jspdf: {
    jsPDF: new (opts: Record<string, unknown>) => {
      addPage(): void;
      addImage(
        imageData: string,
        format: string,
        x: number,
        y: number,
        width: number,
        height: number
      ): void;
      output(type: string): string;
    };
  };
};

export default defineComponent({
  name: "BienBanKiemNghiemViewer",

  components: { PdfViewerPage, PdfDocumentHeader },

  setup() {
    const { moment } = useDateTime();
    return { moment };
  },

  data() {
    return {
      bienBanData: null as IBienBanResponseBase | null,
      pdfSource: null as string | null,
      isGenerating: false,
      tenDonVi: "",
      tenDonViCha: "",
    };
  },

  computed: {
    pdfFileName(): string {
      if (!this.bienBanData) return "bien-ban-kiem-nghiem.pdf";
      return `bien-ban-kiem-nghiem-${this.bienBanData.so_bien_ban}.pdf`;
    },

    ngayBienBanDisplay(): string {
      if (this.bienBanData?.ngay_bien_ban) {
        const date = new Date(this.bienBanData.ngay_bien_ban);
        return `Ngày ${date.getDate()} tháng ${
          date.getMonth() + 1
        } năm ${date.getFullYear()}`;
      }
      return ".........................";
    },

    tenGiamDocDisplay(): string {
      return this.bienBanData?.ten_giam_doc
        ? this.bienBanData.ten_giam_doc
        : ".........................";
    },

    thanhPhanItems(): {
      thuTu: number;
      hoTen: string;
      chucVu: string;
      thanhPhan: string;
    }[] {
      if (!this.bienBanData?.thanh_phan_hoi_dong) return [];
      try {
        return JSON.parse(this.bienBanData.thanh_phan_hoi_dong).filter(
          ({
            hoTen,
            chucVu,
            thanhPhan,
          }: {
            hoTen: string;
            chucVu: string;
            thanhPhan: string;
          }) => !!hoTen || !!chucVu || !!thanhPhan
        );
      } catch (e) {
        console.error(e);
        return [];
      }
    },

    ngayHopDongDisplay(): string {
      if (this.bienBanData?.hop_dong?.ngay_ky) {
        return this.moment(this.bienBanData.hop_dong.ngay_ky).format(
          "DD/MM/YYYY"
        );
      }
      return ".......................";
    },

    printPaperStyle(): Record<string, string> {
      return getPdfPrintContentStyle();
    },

    hiddenTemplateStyle(): Record<string, string> {
      return { width: `${getPdfContentWidthPx()}px` };
    },
  },

  methods: {
    formatQuantity,
    formatUnitPrice,

    donGiaVatTuDisplay(vatTu: IVatTuBienBanKiemNghiemResponseBase): string {
      const vatTuNhapKho =
        this.bienBanData?.phieu_nhap_kho?.vat_tu_nhap_kho?.find(
          ({ id }) => id === vatTu.id
        );
      return formatUnitPrice(+(vatTuNhapKho?.pivot?.don_gia ?? 0));
    },

    async loadAndGenerate() {
      const id = Number(this.$route.params.id);
      if (!id) return;

      try {
        this.isGenerating = true;
        const { data: response } = await BienBanKiemNghiemService.get(id);
        this.bienBanData = response.data;
        const { tenDonVi, tenDonViCha } = pickDonViPdfExtra(response.extra);
        this.tenDonVi = tenDonVi;
        this.tenDonViCha = tenDonViCha;

        await this.$nextTick();
        await this.$nextTick();

        await this.generatePdf();
      } catch (e) {
        swalErrNotification(e, "Có lỗi khi tải biên bản kiểm nghiệm");
      } finally {
        this.isGenerating = false;
      }
    },

    async generatePdf() {
      const el = (this.$refs.printArea as HTMLElement)?.querySelector(
        ".printing-paper"
      ) as HTMLElement | null;
      if (!el) return;

      const canvas = await html2canvas(el, {
        scale: 2,
        useCORS: true,
        scrollX: 0,
        scrollY: 0,
        backgroundColor: "#ffffff",
      });

      const { top, bottom, left, right } = PDF_CONTENT_MARGINS;
      const pageWidth = PDF_A4.widthMm;
      const pageHeight = PDF_A4.heightMm;
      const usableWidth = pageWidth - left - right;

      const imgWidthMm = usableWidth;
      const imgHeightMm = (canvas.height / canvas.width) * imgWidthMm;

      const pdf = new window.jspdf.jsPDF({
        unit: "mm",
        format: "a4",
        orientation: "portrait",
      });
      const usableHeight = pageHeight - top - bottom;

      let yOffset = 0;
      while (yOffset < imgHeightMm) {
        if (yOffset > 0) pdf.addPage();

        const sliceHeightMm = Math.min(usableHeight, imgHeightMm - yOffset);
        const sliceHeightPx = (sliceHeightMm / imgHeightMm) * canvas.height;
        const sliceYPx = (yOffset / imgHeightMm) * canvas.height;

        const sliceCanvas = document.createElement("canvas");
        sliceCanvas.width = canvas.width;
        sliceCanvas.height = sliceHeightPx;
        const ctx = sliceCanvas.getContext("2d");
        if (ctx) {
          ctx.drawImage(canvas, 0, -sliceYPx);
        }

        const sliceData = sliceCanvas.toDataURL("image/jpeg", 0.98);
        pdf.addImage(sliceData, "JPEG", left, top, imgWidthMm, sliceHeightMm);

        yOffset += usableHeight;
      }

      this.pdfSource = pdf.output("datauristring");
    },
  },

  mounted() {
    this.loadAndGenerate();
  },
});
